import { ForgeApi, IJob } from '../api/services/ForgeApi';

const POLL_INTERVAL = 2000; // ms
// How long polls may keep failing before the job is given up on. A single
// 404 or 502 is routine while Forge's load balancer or a task restarts, and
// the job carries on regardless.
const CONTACT_TIMEOUT = 120000; // ms

/**
 * Wait for a Forge job to finish, for callers that run jobs in sequence and so
 * need a promise rather than a polling component. Follows the same rules as
 * OrganixSimulateRemoteForge: a failed job ends at once, failed polls are
 * retried until Forge has been unreachable for CONTACT_TIMEOUT, and a 500
 * ("not ready yet") keeps polling.
 *
 * Resolves with the completed job, or null once isCancelled() returns true.
 * Rejects with an Error whose message can be shown to the user.
 */
const waitForForgeJob = (jobID: string, isCancelled: () => boolean, onProgress: (percent: number) => void): Promise<IJob | null> =>
  new Promise((resolve, reject) => {
    let lastContact = Date.now();

    const poll = async () => {
      if (isCancelled()) { resolve(null); return; }
      try {
        const res = await ForgeApi.getJob(jobID);
        lastContact = Date.now();
        if (isCancelled()) { resolve(null); return; }
        if (res.status === "failed") {
          const reason = res.simulations?.find(s => s.status === "failed")?.error;
          reject(new Error(reason ? `failed: ${reason} (job ${jobID})` : `failed on the server (job ${jobID})`));
          return;
        }
        if (res.status === "completed") {
          resolve(res);
          return;
        }
        onProgress(res.progress ?? 0);
      } catch (err) {
        if (isCancelled()) { resolve(null); return; }
        const statusCode = (err as { response?: { status?: number } })?.response?.status;
        if (statusCode !== 500 && Date.now() - lastContact > CONTACT_TIMEOUT) {
          reject(new Error(`lost contact with the simulation server (job ${jobID}${statusCode ? `, HTTP ${statusCode}` : ""})`));
          return;
        }
        console.warn(`Forge poll failed${statusCode ? ` (HTTP ${statusCode})` : ""}, retrying...`);
      }
      // Chained rather than setInterval, so a slow poll never overlaps the next.
      setTimeout(poll, POLL_INTERVAL);
    };

    poll();
  });

/** Download a completed simulation's JSON result. */
const downloadForgeResult = async (url: string): Promise<any> => {
  const response = await fetch(url, {
    method: "GET",
    headers: { "Accept": "application/json" }
  });
  if (!response.ok) {
    throw new Error(`download failed: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

export { waitForForgeJob, downloadForgeResult }
